import React, { useState, useEffect } from "react";
import "./JoinFundraiserForm.css";
import bearWelcoming from "../../images/join_fundraiser_frame.png";
import config from "../../config";
import { useParams } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import {
  decodeQueryParameter,
  encodeQueryParameter,
  getCurrentlyLoggedInUser,
  parseFundraiserID,
} from "../../CommonFunctions";
import { AxiosResponse } from "axios";
import { ParticipantCreationResponse } from "../../Interfaces/FundraiserRole";
import apiClient from "../../services/apiClient";
import fetchWithAuth from "../../services/fetchWithAuth";
import { usePopup } from "../../components/Popup/PopupContext";
import PopupModel from "../../components/Popup/PopupModel";
import { fetchFeatureFlags } from "../../services/fetchFeatureFlags";

import UploadAndViewProfilePhoto from "../../components/Forms/ParticipantPhotos/UploadAndViewProfilePhoto/UploadAndViewProfilePhoto";

const BASE_API = config.API_BASE_URL;

type SecondaryDivision = {
  id: number;
  frontend_id: string;
  division_name: string;
  admin_email: string;
};

type PrimaryDivision = {
  id: number;
  frontend_id: string;
  division_name: string;
  secondary_divisions: SecondaryDivision[];
};

type Participant = {
  id: string;
  first_name: string;
  last_name: string;
};

type ParticipantEntry = {
  first_name: string;
  last_name: string;
  account_profile: number;
  account_id: number;
  primary_division_id: number;
  primaryDivisionName: string;
  secondary_division_id: number;
  secondaryDivisionName: string;
  profile_picture: string | null;
  fundraiser_id: number;
  error?: boolean;
};
const AddParticipants: React.FC = () => {
  const [loading, setLoading] = useState(true); // New loading state
  const [selectedParticipant, setSelectedParticipant] = useState<string | null>(
    "select"
  );
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [primaryDivision, setPrimaryDivision] = useState<number | "">("");
  const [participantAccountID, setParticipantAccountID] = useState<number>(-1);
  const [secondaryDivision, setSecondaryDivision] = useState<number | "">("");
  const [profile_picture, setProfilePicture] = useState<string | null>(null);
  const [formError, setFormError] = useState<string>("");
  const [accountID, setAccountID] = useState<number>(-1);
  const [supportEmail, setSupportEmail] = useState<string>("");
  const [loggedInUser, setLoggedInUser] = useState<any | null>(null); // State for logged-in user
  const [participantsFromApi, setParticipantsFromApi] = useState<Participant[]>(
    []
  );
  const [participants, setParticipants] = useState<ParticipantEntry[]>([]);
  const [primaryDivisions, setPrimaryDivisions] = useState<PrimaryDivision[]>(
    []
  );
  const [currentSecondaryDivisions, setCurrentSecondaryDivisions] = useState<
    SecondaryDivision[]
  >([]);

  const [flags, setFlags] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadFeatureFlags = async () => {
      const flagData = await fetchFeatureFlags();
      setFlags(flagData);
    };

    loadFeatureFlags();
  }, []);

  const location = useLocation();
  const { fundraiserID } = useParams<{ fundraiserID: string }>();
  const navigate = useNavigate();
  let decryptedFundraiserID: number = -1;
  const { showPopup, hidePopup } = usePopup();
  try {
    decryptedFundraiserID = parseFundraiserID(
      decodeQueryParameter(fundraiserID || "").fundraiser_id
    );
  } catch {
    decryptedFundraiserID = -1;
  }
  useEffect(() => {
    const fundraiserName = location.state?.fundraiser_name;
    if (fundraiserName) {
      document.title = `Join ${fundraiserName}`;
    } else {
      document.title = "Join Fundraiser";
    }
  }, []);

  useEffect(() => {
    const initializeUserAndFetchData = async () => {
      try {
        const user = await getCurrentlyLoggedInUser();
        setLoggedInUser(user);
        setAccountID(user.id);

        if (user) {
          await fetchParticipants(user.id);
          await fetchDivisions();
        }
      } catch (error) {
        console.error("Error initializing user or fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchParticipants = async (accountID: number) => {
      try {
        const response = await fetchWithAuth(
          `${BASE_API}account/account-profiles/${accountID}`
        );
        const apiData = await response.json();

        if (Array.isArray(apiData)) {
          setParticipantsFromApi(apiData);
        } else {
          setParticipantsFromApi([]); // Set an empty array if data is invalid
        }
      } catch (error) {
        console.error("Failed to fetch participants", error);
        setParticipantsFromApi([]);
      }
    };

    const fetchDivisions = async () => {
      try {
        const response = await fetchWithAuth(
          `${BASE_API}division/fundraiser/${decryptedFundraiserID}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch divisions");
        }
        const data: PrimaryDivision[] = await response.json();
        setPrimaryDivisions(data);
      } catch (error) {
        console.error("Failed to fetch divisions", error);
        setPrimaryDivisions([]);
      }
    };
    const fetchSupportEmail = async () => {
      try {
        const response = await fetch(`${BASE_API}config-options/support_email`);
        if (response.ok) {
          const data = await response.json();
          setSupportEmail(data[0].config_value);
        } else {
          throw new Error("Network response was not ok");
        }
      } catch (error) {
        console.error("Failed to fetch support email", error);
      }
    };

    // Only fetch the support email if the fundraiserID is invalid
    if (decryptedFundraiserID === -1) {
      fetchSupportEmail();
    }

    initializeUserAndFetchData();
  }, [decryptedFundraiserID]);

  const handleSelectParticipant = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedId = event.target.value;
    setSelectedParticipant(selectedId);
    setFormError("");
    if (selectedId === "select") {
      resetForm();
      disableForm(true);
    } else if (selectedId === "new") {
      resetForm();
      disableForm(false);
    } else {
      const participant = participantsFromApi.find(
        (p) => Number(p.id) === Number(selectedId)
      );
      if (participant) {
        setFirstName(participant.first_name);
        setLastName(participant.last_name);
        setParticipantAccountID(Number(participant.id));
        setPrimaryDivision("");
        setSecondaryDivision("");
        setProfilePicture(null);
      }
    }
  };
  const disableForm = (shouldDisable: boolean) => {
    const formFields = document.querySelectorAll<
      HTMLInputElement | HTMLSelectElement
    >(".participant-join-form-input, .participant-join-form-select");
    formFields.forEach((field) => {
      if (field.id !== "select-participant") {
        // Don't disable the participant dropdown
        field.disabled = shouldDisable;
      }
    });
  };
  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setPrimaryDivision("");
    setSecondaryDivision("");
    setProfilePicture(null);
    setFormError("");
    setCurrentSecondaryDivisions([]);
    setParticipantAccountID(-1);
  };

  const handlePrimaryDivisionChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setFormError("");
    const selectedPrimaryId = Number(e.target.value);
    setPrimaryDivision(selectedPrimaryId);

    const selectedPrimary = primaryDivisions.find(
      (division) => division.id === selectedPrimaryId
    );
    if (selectedPrimary) {
      setCurrentSecondaryDivisions(selectedPrimary.secondary_divisions);
      setSecondaryDivision("");
    } else {
      setCurrentSecondaryDivisions([]);
    }
  };

  const handleAddParticipant = () => {
    if (!first_name || !last_name || !primaryDivision || !secondaryDivision) {
      setFormError("All fields are required.");
      return;
    }

    const selectedPrimaryDivision = primaryDivisions.find(
      (division) => division.id === Number(primaryDivision)
    );
    const selectedSecondaryDivision = currentSecondaryDivisions.find(
      (division) => division.id === Number(secondaryDivision)
    );

    // Normalize input names (trim and lowercase)
    const normalizedFirstName = first_name.trim().toLowerCase();
    const normalizedLastName = last_name.trim().toLowerCase();

    // Check for duplicates only if "Add new participant" is selected
    if (selectedParticipant === "new") {
      const isDuplicateInAPI = participantsFromApi.some(
        (participant) =>
          participant.first_name.trim().toLowerCase() === normalizedFirstName &&
          participant.last_name.trim().toLowerCase() === normalizedLastName
      );

      if (isDuplicateInAPI) {
        setFormError(
          "This participant is already linked to your account. Please select them from the dropdown instead of `Add New Participant`"
        );
        return;
      }
    }

    // Check for duplicates in the local state
    const isDuplicateInLocal = participants.some(
      (participant) =>
        participant.first_name.trim().toLowerCase() === normalizedFirstName &&
        participant.last_name.trim().toLowerCase() === normalizedLastName
    );

    if (isDuplicateInLocal) {
      setFormError(
        "This participant has already been added to the participant list."
      );
      return;
    }

    // Create a new participant entry
    const newParticipant: ParticipantEntry = {
      first_name,
      last_name,
      fundraiser_id: decryptedFundraiserID,
      account_profile:
        selectedParticipant === "new" ? -1 : participantAccountID,
      account_id: accountID,
      primary_division_id: primaryDivision,
      primaryDivisionName: selectedPrimaryDivision
        ? selectedPrimaryDivision.division_name
        : "",
      secondary_division_id: secondaryDivision,
      secondaryDivisionName: selectedSecondaryDivision
        ? selectedSecondaryDivision.division_name
        : "",
      profile_picture,
    };

    // Add the participant to local state only
    setParticipants((prev) => [...prev, newParticipant]);
    resetForm();
    setSelectedParticipant("select");
  };
  const handleDeleteParticipant = (index: number) => {
    setParticipants((prev) => prev.filter((_, i) => i !== index));
  };

  const displayWarningBeforeContinue = async () => {
    const displayBackButtonWarning = new PopupModel(
      "Warning!",
      (
        <div>
          Please note that once you continue from this page, please do not press
          the back button on your browser, as you will be unable to continue
          again from this screen with the same participants. If you do so,
          please just continue to your dashboard. Emails can be added at a later
          time from the participant dashboard.
        </div>
      ),
      [
        {
          label: "I understand, I won't push the back button.",
          onClick: () => {
            handleContinueButton();
            hidePopup();
          },
        },
      ],
      "40vw"
    );

    showPopup(
      displayBackButtonWarning.header,
      displayBackButtonWarning.body,
      displayBackButtonWarning.buttons,
      displayBackButtonWarning.width
    );
  };

  const handleContinueButton = async () => {
    if (participants.length < 1) {
      setFormError("You must add at least one participant to continue");
    } else {
      const payload = {
        fundraiser_id: decryptedFundraiserID,
        participants: participants,
      };

      try {
        const response: AxiosResponse = await apiClient.post(
          `${BASE_API}fundraiser_roles/participants/add`,
          payload,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        // Handle successful response
        if (response.status === 201) {
          const arrayOfParticipantIDs = (
            response.data as ParticipantCreationResponse[]
          ).reduce<number[]>((acc, participant) => {
            if (participant.participant_profile_id) {
              acc.push(participant.participant_profile_id);
            }
            return acc;
          }, []);

          setParticipants([]); // Clear participants on success

          // Navigate to the next page with the participant IDs
          const participantsQuery = encodeQueryParameter({
            participants: arrayOfParticipantIDs.join(","),
          });
          navigate(`/fundraiser/participant/add-contacts/${participantsQuery}`);
        }
      } catch (error: any) {
        // Handle error response
        if (error.response && error.response.status === 400) {
          const accountProfileErrors =
            error.response.data?.account_profile_errors;

          if (accountProfileErrors && Array.isArray(accountProfileErrors)) {
            // Assuming you have the participant IDs in the errors array
            const participantIdsWithErrors = accountProfileErrors.map(
              (error: any) => error.account_profile_id // Modify based on your error structure
            );
            // Convert the participant IDs from strings to numbers
            const participantIdsWithErrorsAsNumbers =
              participantIdsWithErrors.map((id) => Number(id));

            // Update participants to include error flag
            const updatedParticipants = participants.map((participant) => ({
              ...participant,
              error: participantIdsWithErrorsAsNumbers.includes(
                participant.account_profile
              ),
            }));

            setParticipants(updatedParticipants); // Set updated participants

            const errorMessages =
              "One or more accounts have already been added to this fundraiser";

            setFormError(errorMessages);
          } else {
            // General error
            setFormError(
              "An error occurred while adding participants. Please try again."
            );
          }
        } else {
          // Handle unexpected errors
          setFormError("An unexpected error occurred. Please try again.");
        }
      }
    }
  };
  if (!loggedInUser) {
    return (
      <div className="unauthenticated-container">
        <h2>You are not logged in</h2>
        <button onClick={() => navigate("/login")}>Log In</button>
      </div>
    );
  }
  if (decryptedFundraiserID === -1) {
    return (
      <div className="participant-join-form-error-container">
        <h2>Error: Invalid Fundraiser ID</h2>
        <p>
          We were unable to find the fundraiser. Please try again. If this issue
          persists, please contact our support {supportEmail || ""}
        </p>
        <button onClick={() => navigate("/register-or-shop")}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="participant-join-form-add-participants-container">
      <div className="participant-join-form-image-form-container">
        <div className="participant-join-form-form-image-container">
          <img
            src={bearWelcoming}
            alt="Bear welcoming"
            className="bear-image"
          />
        </div>

        <div className="participant-join-form-form-container">
          <h2>Create Your Profiles</h2>
          <select
            value={selectedParticipant || ""}
            onChange={handleSelectParticipant}
            aria-label="Select existing participant"
            className="participant-join-form-select"
            id="select-participant"
          >
            <option value="select" disabled>
              Select a participant
            </option>

            {participantsFromApi.map((participant) => (
              <option key={participant.id} value={participant.id}>
                {participant.first_name} {participant.last_name}
              </option>
            ))}
            <option value="new">Add New Participant</option>
          </select>

          <div className="participant-join-form-participant-form">
            <div className="participant-join-form-form-row">
              <div className="participant-join-form-form-group">
                <label htmlFor="first_name">First Name</label>
                <input
                  id="first_name"
                  type="text"
                  placeholder="First Name"
                  value={first_name}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="participant-join-form-input"
                  disabled={
                    selectedParticipant === "select" ||
                    selectedParticipant !== "new"
                  }
                />
              </div>

              <div className="participant-join-form-form-group">
                <label htmlFor="last_name">Last Name</label>
                <input
                  id="last_name"
                  type="text"
                  placeholder="Last Name"
                  value={last_name}
                  onChange={(e) => setLastName(e.target.value)}
                  className="participant-join-form-input"
                  disabled={
                    selectedParticipant === "select" ||
                    selectedParticipant !== "new"
                  }
                />
              </div>
            </div>

            <div className="participant-join-form-form-row">
              <div className="participant-join-form-form-group">
                <label htmlFor="primaryDivision">Primary Division</label>
                <select
                  id="primaryDivision"
                  value={primaryDivision || ""}
                  onChange={handlePrimaryDivisionChange}
                  className="participant-join-form-select"
                  disabled={selectedParticipant === "select"}
                >
                  <option value="" disabled>
                    Select Primary Division
                  </option>
                  {primaryDivisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.division_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="participant-join-form-form-group">
                <label htmlFor="secondaryDivision">Secondary Division</label>
                <select
                  id="secondaryDivision"
                  value={secondaryDivision || ""}
                  onChange={(e) => setSecondaryDivision(Number(e.target.value))}
                  className="participant-join-form-select"
                  disabled={
                    selectedParticipant === "select" || !primaryDivision
                  }
                >
                  <option value="" disabled>
                    Select Secondary Division
                  </option>
                  {currentSecondaryDivisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.division_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="participant-join-form-button-error-container">
              <button
                className="participant-join-form-add-participants-button"
                onClick={handleAddParticipant}
                disabled={selectedParticipant === "select"}
              >
                Add Participant
              </button>
              {formError && (
                <p className="participant-join-form-error-message">
                  {formError}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="participant-join-form-participants-list">
        <table className="participant-table">
          <thead>
            <tr>
              {flags.participant_profile_photo && <th>Profile Picture</th>}
              <th>Name</th>
              <th>Primary Division</th>
              <th>Secondary Division</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {participants.map((participant, index) => (
              <tr
                key={index}
                className={
                  participant.error ? "participant-page-error-row" : ""
                }
              >
                {flags.participant_profile_photo && (
                  <td>
                    <UploadAndViewProfilePhoto
                      selectedFundraiserRoleIdInt={participant.account_id}
                    />
                  </td>
                )}
                <td>
                  {participant.first_name} {participant.last_name}
                </td>
                <td>{participant.primaryDivisionName}</td>
                <td>{participant.secondaryDivisionName}</td>
                <td>
                  <button
                    className="delete-participant-button"
                    onClick={() => handleDeleteParticipant(index)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="continue-button-container">
        <button
          className="participant-join-form-continue-button"
          onClick={displayWarningBeforeContinue}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default AddParticipants;
